'use client'

import { useAtom, useSetAtom } from 'jotai'
import { useCallback } from 'react'

import { navigateAfterAuth } from '@/features/auth/lib/post-auth-navigation'
import { getHomePagePath } from '@/features/dashboard/lib/trade-page-path'
import { fetchUserAccounts } from '@/service/account.api'
import { authDialogAtom } from '@/state/auth'
import { clearChatThreadsAtom } from '@/state/chat'
import { AccountStatus } from '@/types/generated/common/account'

export function useAuthDialogControl() {
  const [authDialog, setAuthDialog] = useAtom(authDialogAtom)
  const clearChatThreads = useSetAtom(clearChatThreadsAtom)

  const closeDialog = useCallback(() => {
    setAuthDialog({ open: false, pendingAction: undefined })
  }, [setAuthDialog])

  const handleSuccess = useCallback(
    async (isNewUser = false) => {
      // 1. pendingAction 실행 및 다이얼로그 닫기
      let hasPendingAction = false
      setAuthDialog((prev) => {
        if (prev.pendingAction) {
          hasPendingAction = true
          try {
            prev.pendingAction()
          } catch (error) {
            console.error('Error executing pendingAction:', error)
          }
        }
        return { open: false, pendingAction: undefined }
      })

      // 2. 채팅 초기화
      clearChatThreads()

      // 3. 리다이렉트 처리
      try {
        const accountsData = await fetchUserAccounts()
        const hasActiveAccount = accountsData.accounts.some(
          (acc) => acc.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
        )

        if (isNewUser || !hasActiveAccount) {
          navigateAfterAuth('/onboarding')
        } else if (
          !hasPendingAction &&
          (window.location.pathname === '/login' || window.location.pathname === '/register')
        ) {
          navigateAfterAuth(getHomePagePath())
        }
      } catch (error) {
        console.error('Failed to fetch accounts during auth success:', error)
        navigateAfterAuth('/onboarding')
      }
    },
    [clearChatThreads, setAuthDialog]
  )

  return {
    isOpen: authDialog.open,
    pendingAction: authDialog.pendingAction,
    closeDialog,
    handleSuccess,
  }
}
